/**
 * Citation Verification Tool - Claim Verifier
 * 
 * Verifies that citations actually support the claims being made
 */

import { createFileLogger } from '../../../utils/logger';
import { CommandExecutionContext } from '../../../types/commands';
import {
  InTextCitation,
  FetchResult,
  VerificationResult,
  AlignmentVerdict,
  EvidencePassage,
  OverstatementDetails
} from './types';

const log = createFileLogger('VERIFY_CITATIONS_VERIFIER');

/**
 * Verify all citations
 */
export async function verifyCitations(
  citations: InTextCitation[],
  fetchResults: FetchResult[],
  context: CommandExecutionContext,
  onProgress?: (progress: { completed: number; total: number }) => void
): Promise<VerificationResult[]> {
  log.info('[verifyCitations] Starting verification:', { count: citations.length });
  
  const results: VerificationResult[] = [];
  
  for (let i = 0; i < citations.length; i++) {
    const citation = citations[i];
    
    // Find corresponding fetch result
    const fetchResult = fetchResults.find(f => 
      citation.linkedReferenceIds.includes(f.reference.id)
    );
    
    if (!fetchResult || !fetchResult.resolvedPaper) {
      // Can't verify without paper metadata
      results.push(createErrorVerification(
        citation, 
        !fetchResult ? 'No paper available' : 'Paper could not be resolved'
      ));
      continue;
    }
    
    try {
      const result = await verifyClaim(citation, fetchResult, context);
      // Validate the result has a verdict
      if (!result.verdict) {
        log.error('[verifyCitations] Verification returned no verdict:', {
          citationKey: citation.citationKey,
          result
        });
        results.push(
          createErrorVerification(
            citation,
            'Verification completed but no verdict was determined'
          )
        );
      } else {
        results.push(result);
      }
    } catch (error) {
      log.error('[verifyCitations] Verification failed with error:', {
        citationKey: citation.citationKey,
        error: error instanceof Error ? error.message : String(error)
      });
      results.push(
        createErrorVerification(
          citation,
          error instanceof Error ? error.message : 'Verification failed'
        )
      );
    }
    
    if (onProgress) {
      onProgress({ completed: i + 1, total: citations.length });
    }
  }
  
  return results;
}

/**
 * Verify a single claim-citation pair
 */
async function verifyClaim(
  citation: InTextCitation,
  fetchResult: FetchResult,
  context: CommandExecutionContext
): Promise<VerificationResult> {
  log.info('[verifyClaim] Verifying:', {
    citationKey: citation.citationKey,
    paperTitle: fetchResult.resolvedPaper.title.substring(0, 50)
  });
  
  try {
    // Step 1: Search paper for evidence
    const evidencePassages = await searchForEvidence(
      citation,
      fetchResult,
      context
    );
    
    log.info('[verifyClaim] Evidence passages found:', {
      count: evidencePassages.length,
      hasAbstract: evidencePassages.some(p => p.section === 'Abstract')
    });
    
    // Step 2: Use LLM to judge alignment
    const llmJudgment = await getLLMJudgment(
      citation,
      fetchResult.resolvedPaper,
      evidencePassages,
      context
    );
    
    log.info('[verifyClaim] LLM judgment received:', {
      verdict: llmJudgment.verdict,
      confidence: llmJudgment.confidence,
      hasExplanation: !!llmJudgment.explanation
    });
    
    // Step 3: Detect overstatements
    const overstatementDetails = detectOverstatement(
      citation,
      fetchResult.resolvedPaper,
      evidencePassages,
      llmJudgment.verdict
    );
    
    return {
      citation,
      reference: fetchResult.reference,
      resolvedPaper: fetchResult.resolvedPaper,
      verdict: llmJudgment.verdict,
      confidence: llmJudgment.confidence,
      evidencePassages,
      explanation: llmJudgment.explanation,
      evidenceQuote: llmJudgment.evidenceQuote,
      overstatementDetails,
      suggestedRevision: llmJudgment.suggestedRevision,
      issues: llmJudgment.issues,
      verificationMetadata: {
        evidenceSearchScore: calculateEvidenceScore(evidencePassages),
        llmReasoningQuality: llmJudgment.confidence,
        timestamp: new Date()
      }
    };
  } catch (error) {
    log.error('[verifyClaim] Verification failed for citation:', {
      citationKey: citation.citationKey,
      paperTitle: fetchResult.resolvedPaper.title.substring(0, 50),
      error: error instanceof Error ? error.message : String(error)
    });
    throw error; // Re-throw to be caught by verifyCitations
  }
}

/**
 * Search paper for evidence supporting the claim
 */
async function searchForEvidence(
  citation: InTextCitation,
  fetchResult: FetchResult,
  context: CommandExecutionContext
): Promise<EvidencePassage[]> {
  log.info('[searchForEvidence] Searching for evidence');
  
  // If no full text available, use abstract only
  if (fetchResult.status === 'abstract_only' && fetchResult.resolvedPaper.abstract) {
    return [{
      text: fetchResult.resolvedPaper.abstract,
      section: 'Abstract',
      relevanceScore: 0.5 // Default score for abstract-only
    }];
  }
  
  // TODO: Implement semantic search over paper content
  // For now, use abstract if available
  if (fetchResult.resolvedPaper.abstract) {
    return [{
      text: fetchResult.resolvedPaper.abstract,
      section: 'Abstract',
      relevanceScore: 0.7
    }];
  }
  
  return [];
}

/**
 * Get LLM judgment on claim-evidence alignment
 */
async function getLLMJudgment(
  citation: InTextCitation,
  paper: any,
  evidencePassages: EvidencePassage[],
  context: CommandExecutionContext
): Promise<{
  verdict: AlignmentVerdict;
  confidence: number;
  explanation: string;
  evidenceQuote?: string;
  suggestedRevision?: string;
  issues: any[];
}> {
  const prompt = buildVerificationPrompt(citation, paper, evidencePassages);
  
  const { API_BASE_URL } = await import('../../../api/config');
  const authToken = localStorage.getItem('authToken');
  
  const response = await fetch(`${API_BASE_URL}/v2/claude/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify({
      query: prompt,
      context: {
        spaceId: context.spaceId || 'default',
        windowContents: {},
        explicitContent: []
      },
      conversation_history: [],
      model: 'claude-sonnet-4-5',
      temperature: 0.2 // Low temperature for consistent judgments
    })
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`LLM judgment failed: ${response.status} - ${errorText}`);
  }
  
  // Read the streaming response and accumulate text
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Failed to get response reader');
  }
  
  const decoder = new TextDecoder();
  let buffer = '';
  let fullText = '';
  let chunkCount = 0;
  
  try {
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;
      
      buffer += decoder.decode(value, { stream: true });
      
      // Process complete lines (NDJSON format)
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer
      
      for (const line of lines) {
        if (!line.trim()) continue;
        
        chunkCount++;
        
        try {
          const parsed = JSON.parse(line);
          
          // Extract text from streaming events - handle multiple formats
          if (parsed.type === 'content_block_delta' && parsed.delta?.type === 'text_delta') {
            // Standard Anthropic streaming format
            fullText += parsed.delta.text;
          } else if (parsed.type === 'text' && parsed.content) {
            // Backend's custom streaming format
            fullText += parsed.content;
          }
        } catch (e) {
          log.warn('[getLLMJudgment] Failed to parse line:', line);
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
  
  log.info('[getLLMJudgment] Stream complete:', {
    totalChunks: chunkCount,
    textLength: fullText.length,
    textPreview: fullText.substring(0, 200)
  });
  
  // Check if we got any response
  if (fullText.length === 0) {
    log.error('[getLLMJudgment] LLM returned empty response');
    throw new Error('LLM returned empty response');
  }
  
  const data = { content: fullText, text: fullText };
  
  // Parse LLM response
  return parseLLMJudgment(data);
}

/**
 * Build verification prompt for LLM
 */
function buildVerificationPrompt(
  citation: InTextCitation,
  paper: any,
  evidencePassages: EvidencePassage[]
): string {
  const evidenceText = evidencePassages
    .map(p => `[${p.section}] ${p.text}`)
    .join('\n\n');
  
  return `You are verifying a citation in a scientific manuscript.

MANUSCRIPT CLAIM:
"${citation.claimText}"

Claim strength language: ${citation.claimStrength}
(e.g., "strong" = uses words like "proves", "demonstrates", "establishes")

CITED PAPER:
Title: ${paper.title}
Authors: ${paper.authors.map((a: any) => a.name).join(', ')}
Year: ${paper.year}
${paper.abstract ? `Abstract: ${paper.abstract}\n` : ''}

EVIDENCE PASSAGES FROM CITED PAPER:
${evidenceText || '(No evidence passages found)'}

TASK:
Determine if the cited paper actually supports the manuscript's claim.

Consider:
1. Does the paper address this topic?
2. Do the findings align with the claim?
3. Is the claim's strength appropriate? (e.g., "proves" requires very strong evidence)
4. Are there population/context differences? (e.g., mice vs humans, in vitro vs clinical)
5. Does the claim omit important caveats mentioned in the paper?

Respond with JSON:
{
  "verdict": "<one of: strongly_supports | supports | weak_support | not_addressed | contradicts | overstated_strength | overstated_generalizability | cherry_picked>",
  "confidence": <0-100>,
  "explanation": "<2-3 sentences explaining your judgment>",
  "evidence_quote": "<direct quote from paper supporting your judgment>",
  "suggestedRevision": "<if overstated, suggest more accurate phrasing>"
}

Respond with ONLY the JSON object.`;
}

/**
 * Parse LLM judgment response
 */
function parseLLMJudgment(llmResponse: any): {
  verdict: AlignmentVerdict;
  confidence: number;
  explanation: string;
  evidenceQuote?: string;
  suggestedRevision?: string;
  issues: any[];
} {
  try {
    const responseText = llmResponse.content || llmResponse.text || JSON.stringify(llmResponse);
    
    log.info('[parseLLMJudgment] Parsing response:', {
      hasContent: !!llmResponse.content,
      hasText: !!llmResponse.text,
      responseTextLength: responseText?.length,
      responsePreview: responseText?.substring(0, 200)
    });
    
    // Try to extract JSON - handle both raw JSON and JSON in code blocks
    let jsonText: string | null = null;
    
    // First try to find JSON in code blocks (```json ... ```)
    const codeBlockMatch = responseText.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
    if (codeBlockMatch) {
      jsonText = codeBlockMatch[1];
    } else {
      // Fall back to finding any JSON object
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        jsonText = jsonMatch[0];
      }
    }
    
    if (!jsonText) {
      log.error('[parseLLMJudgment] No JSON found in response:', responseText);
      throw new Error('No JSON found in LLM response');
    }
    
    const parsed = JSON.parse(jsonText);
    
    log.info('[parseLLMJudgment] Parsed JSON:', {
      hasVerdict: !!parsed.verdict,
      verdict: parsed.verdict,
      confidence: parsed.confidence,
      hasExplanation: !!parsed.explanation
    });
    
    // Validate verdict is present and valid
    if (!parsed.verdict) {
      log.error('[parseLLMJudgment] Missing verdict in parsed response:', parsed);
      throw new Error('LLM response missing verdict field');
    }
    
    return {
      verdict: parsed.verdict as AlignmentVerdict,
      confidence: parsed.confidence || 50,
      explanation: parsed.explanation || 'No explanation provided',
      evidenceQuote: parsed.evidence_quote,
      suggestedRevision: parsed.suggestedRevision,
      issues: []
    };
  } catch (error) {
    log.error('[parseLLMJudgment] Failed to parse LLM response:', {
      error: error instanceof Error ? error.message : String(error),
      responsePreview: llmResponse?.content?.substring(0, 200) || llmResponse?.text?.substring(0, 200)
    });
    throw error;
  }
}

/**
 * Detect overstatements
 */
function detectOverstatement(
  citation: InTextCitation,
  paper: any,
  evidencePassages: EvidencePassage[],
  verdict: AlignmentVerdict
): OverstatementDetails | undefined {
  if (verdict === 'overstated_strength') {
    return {
      type: 'strength',
      manuscriptLanguage: citation.claimText,
      paperLanguage: evidencePassages[0]?.text || '',
      specificIssue: 'Claim uses stronger language than evidence warrants',
      severity: 'major'
    };
  }
  
  if (verdict === 'overstated_generalizability') {
    return {
      type: 'generalizability',
      manuscriptLanguage: citation.claimText,
      paperLanguage: evidencePassages[0]?.text || '',
      specificIssue: 'Claim generalizes beyond the scope of the study',
      severity: 'major'
    };
  }
  
  return undefined;
}

/**
 * Calculate evidence search score
 */
function calculateEvidenceScore(passages: EvidencePassage[]): number {
  if (passages.length === 0) return 0;
  
  const avgRelevance = passages.reduce((sum, p) => sum + p.relevanceScore, 0) / passages.length;
  return avgRelevance;
}

/**
 * Create error verification result
 */
function createErrorVerification(
  citation: InTextCitation,
  errorMessage: string
): VerificationResult {
  return {
    citation,
    reference: {
      id: '',
      listPosition: 0,
      rawText: '',
      parsed: {},
      parseConfidence: 0
    },
    verdict: 'error',
    confidence: 0,
    evidencePassages: [],
    explanation: errorMessage,
    issues: [{ type: 'no_evidence_found', message: errorMessage }],
    verificationMetadata: {
      evidenceSearchScore: 0,
      llmReasoningQuality: 0,
      timestamp: new Date()
    }
  };
}

